import type {} from '@mui/lab/themeAugmentation';
import { PaletteOptions, createTheme } from '@mui/material';

/**
 * Semantic chart palette. The type and the reason it exists live in core/material-ui.d.ts.
 *
 * KEEP IN SYNC with libs/shared-ui/providers/themeProvider/theme.ts. That theme, not this one, is
 * what SharedUiProvider hands the running app; this one backs the RTL render helper and
 * ui/timePicker. Both files already duplicate the whole palette, so `chart` follows suit rather
 * than inventing a cross-package import that the jest shared-ui mock would then have to fake.
 */
export type ChartPalette = {
  /** Categorical series colours, applied in order and wrapped modulo their length. */
  series: string[];
  ok: string;
  warn: string;
  bad: string;
  neutral: string;
  grid: string;
};

export const chartPalette: ChartPalette = {
  series: ['#2D6CE6', '#06D393', '#7B61FF', '#0FA3B1', '#F5A623', '#ED715D', '#4C8DF6', '#A4AAC4'],
  ok: '#06D393',
  warn: '#F5A623',
  bad: '#ED715D',
  neutral: '#A4AAC4',
  grid: '#DEE0EA',
};

export const theme = createTheme({
  palette: {
    primary: {
      main: '#2D6CE6',
    },
    secondary: {
      main: '#DCDDF3',
    },
    background: {
      default: '#F9F9F9',
      paper: '#FFFFFF',
    },
    info: {
      main: '#8A91A8',
      light: '#989daf',
    },
    text: {
      primary: '#383838',
      secondary: '#242424',
    },
    warning: {
      main: '#FF3D3D',
    },
    success: {
      main: '#06D393',
    },
    error: {
      main: '#ed715d',
      light: '#FEF5F3',
    },
    action: {
      selected: '#495171',
      disabledOpacity: 0.5,
    },
    grey: {
      100: '#f8f8f8',
      200: '#f3f4f9',
      300: '#dee0ea',
      500: '#a4aac4',
      600: '#8288a2',
      700: '#4c4c4c',
      800: '#979797',
      900: '#495171',
      A200: '#f3f4f9',
      A400: '#F4F4F4',
      A700: '#5e7388',
    },
    chart: chartPalette,
  } as PaletteOptions,
  typography: {
    fontFamily: 'Montserrat',
    h2: {
      fontSize: 33,
      fontWeight: 800,
    },
    h3: {
      fontSize: 26,
      fontWeight: 800,
    },
    h4: {
      fontSize: 22,
      fontWeight: 700,
    },
    h5: {
      fontSize: 18,
      fontWeight: 700,
    },
    h6: {
      fontSize: 16,
      fontWeight: 600,
    },
    button: {
      fontSize: 16,
      fontWeight: 700,
      textTransform: 'initial',
    },
    body1: {
      fontWeight: 500,
      fontSize: 14,
    },
    body2: {
      fontWeight: 500,
      fontSize: 12,
    },
    subtitle2: {
      fontSize: 12,
      color: '#979797',
      fontWeight: 700,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: 16,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#383838',
          color: '#fff',
          fontSize: 12,
        },
        arrow: {
          color: '#383838',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#383838',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#383838',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiPopper: {
      defaultProps: {
        sx: {
          '& .MuiPaper-root': {
            backgroundColor: '#FFFFFF',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(45, 108, 230, 0.08)',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(45, 108, 230, 0.12)',
          },
        },
      },
    },
  },
});
